import { existsFilter } from '@iotv/core-services';
import { isOnDemandProgrammeConstructorProps, OnDemandProgramme } from '@iotv/datamodel-core';
import { isNonNaNNumber, isValidBusinessObject } from '@iotv/iotv-v3-types';
import { OnDemandScheduler } from '@iotv/lfc-devices';
import dayjs from 'dayjs';
import { createContext } from 'react';
import { getColorCodeForWindow } from '../Components/Tabs/WindowAllocation/colorCodes';
import { ondemandSchedulerDAO } from '../data/dataAccessObject';
const debug = process.env.REACT_APP_DEBUG && false;
export const defaultOnDemandProgrammeReducerState = {
    programme: null,
    scheduler: null,
    schedulerHash: null,
    tableRows: null,
    gridRows: null,
};
const isOnDemandProgrammeReducerSetOnDemandProgrammeAction = (x) => {
    const c = x;
    return !!c && c.type === 'SetProgramme' && isValidBusinessObject(c.value.programme);
};
const isOnDemandProgrammeReducerMutateOnDemandProgrammeAction = (x) => {
    const c = x;
    return !!c && c.type === 'MutateProgramme' && isOnDemandProgrammeConstructorProps(c.value.update);
};
const isOnDemandProgrammeReducerRecalculateAction = (x) => {
    const c = x;
    return !!c && c.type === 'Recalculate' && c.value === null;
};
const isOnDemandProgrammeReducerLinkItemsToWindowsAction = (x) => {
    const c = x;
    return !!c && c.type === 'LinkItemsToWindow' && c.value.items.every(isValidBusinessObject) && isNonNaNNumber(c.value.windowProxy.window);
};
const isOnDemandProgrammeReducerUnLinkItemsFromWindowsAction = (x) => {
    const c = x;
    return !!c && c.type === 'UnlinkItemsFromWindow' && c.value.items.every(isValidBusinessObject);
};
const isOnDemandProgrammeReducerFlushWindowsAction = (x) => {
    const c = x;
    return !!c && c.type === 'FlushWindows' && c.value?.windowProxies instanceof Array && c.value.windowProxies.every((x) => isNonNaNNumber(x.window));
};
const isOnDemandProgrammeReducerRemoveWindowsAction = (x) => {
    const c = x;
    return !!c && c.type === 'RemoveWindow' && isNonNaNNumber(c.value.windowProxy?.window);
};
const isOnDemandProgrammeReducerInsertWindowAboveAction = (x) => {
    const c = x;
    return !!c && c.type === 'InsertWindowAbove' && isNonNaNNumber(c.value.windowProxy?.window);
};
const isOnDemandProgrammeReducerInsertWindowBelowAction = (x) => {
    const c = x;
    return !!c && c.type === 'InsertWindowBelow' && isNonNaNNumber(c.value.windowProxy?.window);
};
const isResetAction = (x) => {
    const c = x ?? {};
    return c.type === 'Reset' && !!c.value?.scheduler && c.value.scheduler instanceof OnDemandScheduler;
};
export const getOnDemandProgrammeReducer = () => (prev, action) => {
    debug && console.log(`OnDemandProgrammeReducer got action`, action);
    const update = { ...prev };
    if (isOnDemandProgrammeReducerSetOnDemandProgrammeAction(action)) {
        const { value: { programme }, } = action;
        update.programme = programme;
        const schedulerNotPresent = !update.scheduler;
        /** THIS IS UNDER REFACTOR - CURRENTLY TREATING ALL SET ACTIONS HERE AS NEW CONTEXT IS WORKING
         * This is to ensure that the reducer knows the exact context that the AWS Logic has. eg without, it cannon know lastPersistedState
         */
        const schedulerOnDemandProgrammeChanged = update.scheduler?.programme.sk !== update.programme.sk;
        /** if scheduler not present OR the scheduler has a different rolling programme, replace the scheduler */
        if (schedulerNotPresent || schedulerOnDemandProgrammeChanged) {
            const scheduler = new OnDemandScheduler({ dataAccess: ondemandSchedulerDAO, programme });
            update.scheduler = scheduler;
            update.tableRows = [];
            update.gridRows = [];
            debug && console.log('INFO OnDemandProgrammeReducer reset context on', { schedulerNotPresent, schedulerOnDemandProgrammeChanged });
        }
        else if (update.scheduler) {
            const scheduler = new OnDemandScheduler({ dataAccess: ondemandSchedulerDAO, programme });
            update.scheduler = scheduler;
            update.tableRows = [];
            update.gridRows = [];
            debug && console.log('INFO OnDemandProgrammeReducer reset context on', { schedulerNotPresent, schedulerOnDemandProgrammeChanged });
        }
        Object.assign(update, recalculate(update));
    }
    else if (isOnDemandProgrammeReducerMutateOnDemandProgrammeAction(action)) {
        update.programme = new OnDemandProgramme(action.value.update);
        if (update.scheduler) {
            // this is the reverse of normal but looks OK as scheduler is updated before recalculate
            update.scheduler.programme = update.programme;
        }
        Object.assign(update, recalculate(update));
    }
    else if (isOnDemandProgrammeReducerLinkItemsToWindowsAction(action)) {
        const { windowProxy: { window }, items, } = action.value;
        if (update.scheduler) {
            items.forEach((item) => update.scheduler?.allocateItemToWindowByIdx(item, window));
            update.programme = update.scheduler.programme;
            Object.assign(update, recalculate(update));
        }
    }
    else if (isOnDemandProgrammeReducerUnLinkItemsFromWindowsAction(action)) {
        const { items } = action.value;
        /* Note this requires items to be selected in Tree and will remove from all Windows */
        if (update.scheduler) {
            items.forEach((item) => update.scheduler?.deAllocateItemFromWindows(item));
            update.programme = update.scheduler.programme;
            Object.assign(update, recalculate(update));
        }
    }
    else if (isOnDemandProgrammeReducerFlushWindowsAction(action)) {
        const { windowProxies } = action.value;
        if (update.scheduler) {
            for (let windowProxy of windowProxies) {
                update.scheduler.flushWindow(windowProxy.window);
            }
            update.programme = update.scheduler.programme;
            Object.assign(update, recalculate(update));
        }
    }
    else if (isOnDemandProgrammeReducerRemoveWindowsAction(action)) {
        const { windowProxy } = action.value;
        if (update.scheduler) {
            update.scheduler.removeWindow(windowProxy.window);
            update.programme = update.scheduler.programme;
            Object.assign(update, recalculate(update));
        }
    }
    else if (isOnDemandProgrammeReducerInsertWindowAboveAction(action)) {
        const { windowProxy } = action.value;
        if (update.scheduler) {
            update.scheduler.loadNewWindow(windowProxy.window - 1);
            update.programme = update.scheduler.programme;
            Object.assign(update, recalculate(update));
        }
    }
    else if (isOnDemandProgrammeReducerInsertWindowBelowAction(action)) {
        const { windowProxy } = action.value;
        if (update.scheduler) {
            update.scheduler.loadNewWindow(windowProxy.window);
            update.programme = update.scheduler.programme;
            Object.assign(update, recalculate(update));
        }
    }
    else if (isOnDemandProgrammeReducerRecalculateAction(action)) {
        Object.assign(update, recalculate(update));
    }
    else if (isResetAction(action)) {
        const { scheduler } = action.value;
        update.tableRows = [];
        update.gridRows = [];
        update.programme = scheduler.programme;
        update.scheduler = scheduler;
        update.schedulerHash = scheduler.context.getHash();
        Object.assign(update, recalculate(update));
    }
    else {
        console.log(`WARN: action fell through OnDemandProgrammeReducer`, action);
    }
    update.schedulerHash = update.scheduler?.context.getHash() ?? null;
    return update;
};
const recalculate = (state) => {
    const { programme } = state;
    const update = { ...state };
    const scheduler = update.scheduler;
    if (programme && scheduler) {
        update.tableRows = scheduleToDataTableRows({ programme, scheduler });
        update.gridRows = scheduleToDataGridRows({ programme, scheduler });
        update.programme = programme;
        debug && console.log(`OnDemandProgrammeReducer has rows`, update.tableRows);
    }
    return update;
};
const scheduleToDataTableRows = ({ programme, scheduler }) => {
    const programmeConfigs = Array.from({ length: programme.windows * programme.repeats }, (v, i) => i).map((w) => programme.getOnDemandProgrammeConfigForWindow(w));
    const schedules = programmeConfigs.map((config) => {
        if (config) {
            const initial = dayjs().hour(0).minute(0).second(0).millisecond(0);
            const start = dayjs(initial).add(config.utc_start_hour, 'hours').add(config.utc_start_min, 'minutes');
            const end = dayjs(initial)
                .add(config.utc_start_hour, 'hours')
                .add(config.utc_start_min + config.duration_mins, 'minutes');
            return {
                enabled: true,
                dateRange: [start.toDate(), end.toDate()],
            };
        }
    });
    const rows = schedules.filter(existsFilter).map((range, i) => {
        const [from, to] = range.dateRange;
        const window = i % programme.windows;
        const scheduledItemNames = scheduler.getItemsForWindow(window)?.map((i) => i.name);
        return [
            'Sequence',
            scheduledItemNames?.length ? scheduledItemNames.join(', ') : '(No items)',
            getColorCodeForWindow(window),
            from,
            to,
        ];
    });
    return rows;
};
const scheduleToDataGridRows = ({ programme, scheduler }) => {
    const windows = scheduler.getAllWindows();
    const rows = windows
        .map((window) => {
        const config = programme.getOnDemandProgrammeConfigForWindow(window.window);
        if (config) {
            const start = dayjs().hour(config.utc_start_hour).minute(config.utc_start_min).second(0);
            const entities = scheduler.getItemsForWindow(window.window);
            return {
                id: window.id,
                window: window.window,
                delay: programme.windowGapDuration,
                duration: programme.windowDuration,
                startTime: start.valueOf(),
                entities: entities ?? [],
            };
        }
    })
        .filter(existsFilter);
    return rows;
};
export const OnDemandProgrammeContext = createContext([
    defaultOnDemandProgrammeReducerState,
    (next) => {
        throw new Error('Reducer not initialized');
    },
]);
